import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Sticky from 'react-stickynode';

import {
  generateGraphicBannerElement,
  generateFreeHtmlElement,
  generateRhsBanner,
} from 'apps/scribe/scribeContentProvider';

import OfferDetailsPageMetadata from './meta/OfferDetailsPageMetadata';
import DesktopBreadcrumbs from 'apps/catalogue/components/breadcrumbs/DesktopBreadcrumbs';
import DisplayOptions from './DisplayOptions';
import ConditionalWrapper from 'utils/ConditionalWrapper';
import EnforceLocation from 'apps/meta/containers/EnforceLocation';
import CriteoPageAnalytics from '[theme-lib]/apps/analytics/containers/CriteoPageAnalytics';
import ExternalProductsContainer from '[theme-lib]/apps/externalProducts/containers/ExternalProductsContainer';
import ImpressionsPageType from 'apps/gtm/containers/ImpressionsPageType';
import MainFopContainer from 'apps/catalogue/containers/MainFopContainer';
import NotFound from 'widgets/NotFound/NotFound';
import MobileBreadcrumbs from '../containers/MobileBreadcrumbs';
import OfferType from 'apps/products/models/OfferType';
import PageStatistic from 'apps/analytics/components/PageStatistic';
import PageViewType from 'apps/analytics/components/PageViewType';
import RecommendedFopContainer from '../containers/RecommendedFopContainer';
import SortOption from '../models/SortOption';
import TotalProductNumber from './TotalProductNumber';
import OfferDetailsTopBarSponsoredItems from 'apps/catalogue/containers/OfferDetailsTopBarSponsoredItems';

import { isBreadcrumbsWithNewUI } from 'lib/features';

import ExtSectionTypes from 'apps/externalProducts/ExtSectionTypes';
import gtmPageTypes from 'apps/gtm/utils/gtmPageTypes';

import { getOfferLink } from '../links';
import { pageTypes } from 'apps/analytics';
import PageViewEvent from 'apps/analytics/containers/PageViewEvent';

import {
  VIEW_TYPE_PREFERENCE_LIST,
  VIEW_TYPE_PREFERENCE_SHELF,
} from '../actions/catalogue';

const EMPTY_OBJECT = {};
const offersBreadcrumb = { name: 'Offers', link: '/on-offer' };

export default class OfferDetailsPage extends PureComponent {
  static propTypes = {
    betaFeatures: PropTypes.array,
    fopViewPreference: PropTypes.oneOf([VIEW_TYPE_PREFERENCE_LIST, VIEW_TYPE_PREFERENCE_SHELF]),
    productsPage: PropTypes.shape({
      pageParameters: PropTypes.shape({
        id: PropTypes.string.isRequired,
        shortDescription: PropTypes.string.isRequired,
      }).isRequired,
      generalPageInfo: PropTypes.object,
      outOfStockStatistics: PropTypes.array,
      mainFopCollection: PropTypes.object.isRequired,
      recommendedFopCollection: PropTypes.object,
      longDescription: PropTypes.string,
      shortDescription: PropTypes.string,
      scribeContent: PropTypes.shape({
        topRhsBannerPage: PropTypes.shape({
          topBanner: PropTypes.object,
          aboveYmlRhsBanner: PropTypes.object,
          belowYmlRhsBanner: PropTypes.object,
          freeHtmlFiles: PropTypes.array,
        }),
        pageTitle: PropTypes.string,
        pageMetaDescription: PropTypes.string,
      }),
      beacons: PropTypes.objectOf(PropTypes.string),
    }),
    basePath: PropTypes.string.isRequired,
    routeString: PropTypes.string.isRequired,
  };

  generateRecommendedFopList() {
    const { betaFeatures, productsPage = {} } = this.props;
    const { recommendedFopCollection = {} } = productsPage;

    if (betaFeatures && betaFeatures.includes('EXTERNAL_SIDEBAR_FOP_RECOMMENDATIONS')) {
      return (
        <ExternalProductsContainer
          className="sidebar-element"
          name={ExtSectionTypes.OFFER_DETAILS_SIDEBAR_RECOMMENDED}
          desktopOnly
        />
      );
    }

    return (
      get(recommendedFopCollection, 'sections')
        ? (
          <RecommendedFopContainer
            betaFeatures={betaFeatures}
            fopCollection={recommendedFopCollection}
          />
        )
        : null
    );
  }

  generateFreeHtmlElement() {
    const { scribeContent } = this.props.productsPage;
    const freeHtmlFiles = get(scribeContent, 'topRhsBannerPage.freeHtmlFiles');
    if (freeHtmlFiles) {
      const freeHtmlElements = generateFreeHtmlElement(freeHtmlFiles, scribeContent.topRhsBannerPage);
      return <div className="scbFreeHtml">{freeHtmlElements}</div>;
    }
    return null;
  }

  get sidebarCriteoFopList() {
    const { productsPage: { sidebarSponsoredItems } = {} } = this.props;

    return (
      get(sidebarSponsoredItems, 'sections.length')
        ? (
          <RecommendedFopContainer
            fopCollection={sidebarSponsoredItems}
            maxDisplayFops={Number.MAX_SAFE_INTEGER}
            title="Top Brand Offers"
          />
        )
        : null
    );
  }

  renderSidebar = () => {
    const { isStickySidebarEnabled, productsPage } = this.props;
    const { scribeContent } = productsPage;
    const scribeParentContainer = get(scribeContent, 'topRhsBannerPage', EMPTY_OBJECT);
    const isSidebarCriteoFopList = this.sidebarCriteoFopList !== null;

    return (
      isSidebarCriteoFopList ? (
        <div className="sidebar">
          <ConditionalWrapper
            condition={isStickySidebarEnabled}
            wrapper={children => (
              <Sticky
                top=".hd-header"
                bottomBoundary="#main-content"
                activeClass="gn-sticky-sidebar--active"
                enableTransforms={false}
                innerZ={21}
              >
                {children}
              </Sticky>
            )}
          >
            {this.sidebarCriteoFopList}
          </ConditionalWrapper>
        </div>
      ) : (
        <div className="sidebar-right">
          {this.generateFreeHtmlElement()}
          {generateRhsBanner(scribeParentContainer.aboveYmlRhsBanner, scribeParentContainer)}
          <ExternalProductsContainer
            className="sidebar-element"
            name={ExtSectionTypes.OFFER_DETAILS_SIDEBAR}
            desktopOnly
          />
          {this.generateRecommendedFopList()}
          {generateRhsBanner(scribeParentContainer.belowYmlRhsBanner, scribeParentContainer)}
        </div>
      )
    );
  };

  render() {
    const { productsPage, betaFeatures, fopViewPreference, basePath, routeString } = this.props;
    const excludedSort = [
      SortOption.PRICE_PER_ASC,
      SortOption.PRICE_PER_DESC,
    ];
    if (!productsPage) {
      return (
        <NotFound>
          <p>No offer identified by &quot;{routeString}&quot; has been found.</p>
        </NotFound>
      );
    }

    const { pageParameters, generalPageInfo, outOfStockStatistics,
      mainFopCollection, longDescription, shortDescription, scribeContent, beacons = {} } = productsPage;
    const scribeParentContainer = get(scribeContent, 'topRhsBannerPage', EMPTY_OBJECT);
    const pageViewGTMData = {
      pageType: pageTypes.OFFER_DETAILS,
    };

    return (
      <div className="sidebar-layout">
        <PageViewType pageType={pageTypes.OFFER_DETAILS} />
        <ImpressionsPageType pageType={gtmPageTypes.Offers} />
        <PageStatistic field="OFFER_PROMO_ID" value={pageParameters.id} />
        <PageStatistic field="FOP_VIEW_PREFERENCE" value={fopViewPreference} />
        <PageViewEvent eventData={pageViewGTMData} />
        <EnforceLocation
          href={getOfferLink({ id: pageParameters.id, type: OfferType.REGULAR, text: shortDescription })}
        />
        <OfferDetailsPageMetadata
          basePath={basePath}
          pageParameters={pageParameters}
        />
        {this.renderSidebar()}
        <div className="main-column">
          <DesktopBreadcrumbs
            items={[offersBreadcrumb, { name: shortDescription }]}
            withNewUI={isBreadcrumbsWithNewUI(betaFeatures)}
          />

          {generateGraphicBannerElement(scribeParentContainer.topBanner, scribeParentContainer)}
          <ExternalProductsContainer name={ExtSectionTypes.OFFER_DETAILS_BANNER} />
          <OfferDetailsTopBarSponsoredItems productsPage={productsPage} />
          <div className="catHeaderBar">
            <div className="desktop-product-count show-for-xlarge">
              <TotalProductNumber itemsCount={mainFopCollection.totalFops} />
            </div>
            <MobileBreadcrumbs
              itemsCount={mainFopCollection.totalFops}
              text={shortDescription}
              showBackLink
            />
            <DisplayOptions
              pageParameters={pageParameters}
              betaFeatures={betaFeatures}
              excludedSort={excludedSort}
              displaySort
            />
          </div>
          <div className="promo-header">
            <h1>{shortDescription}</h1>
            <p>{longDescription}</p>
          </div>
          <MainFopContainer
            betaFeatures={betaFeatures}
            fopCollection={mainFopCollection}
            generalPageInfo={generalPageInfo}
            gtmPageType="Offers"
            outOfStockStatistics={outOfStockStatistics}
            pageParameters={pageParameters}
            routeString={routeString}
          />
          <CriteoPageAnalytics betaFeatures={betaFeatures} beacons={beacons} />
        </div>
      </div>
    );
  }
}
